import * as React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Add, CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';

import { EBuildingAssignments } from 'enums';
import { useUserAssignedBuildingsFormManager } from './user-assigned-buildings-form.hook';
import { AssignedBuildingList, Loading } from 'components';

interface UserAssignedBuildingsFormProps {
  userId: number;
  onInit?: (loading: boolean) => void;
  onSubmitting?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const UserAssignedBuildingsForm = React.forwardRef(
  ({ userId, onInit = () => {}, onSubmitting = () => {}, next }: UserAssignedBuildingsFormProps, ref) => {
    const [{ assignedBuildings, unassignedBuildings, formManager, loading_form, loading_submit }] =
      useUserAssignedBuildingsFormManager({
        userId,
        next,
      });

    React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
    React.useEffect(() => onInit(loading_form), [loading_form]);
    React.useEffect(() => onSubmitting(loading_submit), [loading_submit]);

    const handleSelectAll = () => {
      const allSelected = checkAllBuildingsSelected();

      if (allSelected) {
        formManager.setFieldValue('buildingIds', []);
      } else {
        formManager.setFieldValue(
          'buildingIds',
          unassignedBuildings.map(({ id }) => id),
        );
      }
    };

    const checkAllBuildingsSelected = () => {
      if (formManager.values.buildingIds.length !== unassignedBuildings.length) {
        return false;
      }

      for (let i = 0; i < unassignedBuildings.length; i++) {
        const building = unassignedBuildings[i];

        if (!formManager.values.buildingIds.includes(building.id)) {
          return false;
        }
      }

      return true;
    };

    return (
      <form onSubmit={formManager.handleSubmit}>
        <Grid
          container
          sx={{
            alignItems: 'center',
          }}
          rowSpacing={3}
          columnSpacing={1}
        >
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Assignment</InputLabel>
              <Select
                label="Assignment"
                value={formManager.values.assignment}
                onChange={(event) => formManager.setFieldValue('assignment', event.target.value)}
              >
                <MenuItem value={EBuildingAssignments.CLEANER}>Cleaner</MenuItem>
                <MenuItem value={EBuildingAssignments.SUPERVISOR}>Supervisor</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={10}>
            <Autocomplete
              multiple
              options={[-1, ...unassignedBuildings.map(({ id }) => id)]}
              getOptionLabel={(option) =>
                option === -1 ? 'Select All' : unassignedBuildings.find(({ id }) => id === option)?.address || ''
              }
              value={loading_form ? [] : formManager.values.buildingIds}
              onChange={(event, values) => {
                formManager.setFieldValue('buildingIds', values);
              }}
              renderOption={(props, option, { selected }) => {
                const building = unassignedBuildings.find(({ id }) => id === option);
                const key = building?.id || Date.now();
                const label = building?.address || '';

                return option === -1 ? (
                  <Box key="select-all-options">
                    <Box
                      sx={{
                        height: 42,
                      }}
                    />

                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        width: '100%',
                        bgcolor: 'white',
                        zIndex: 10,
                      }}
                    >
                      <ListItem {...props} onClick={() => handleSelectAll()}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlank fontSize="small" />}
                          checkedIcon={<CheckBox fontSize="small" />}
                          checked={checkAllBuildingsSelected()}
                        />
                        Select All
                      </ListItem>

                      <Divider />
                    </Box>
                  </Box>
                ) : (
                  <ListItem {...props} key={key}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBox fontSize="small" />}
                      checked={selected}
                    />
                    {label}
                  </ListItem>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buildings"
                  error={!!(formManager.touched.buildingIds && formManager.errors.buildingIds)}
                  helperText={formManager.touched.buildingIds && formManager.errors.buildingIds}
                />
              )}
              limitTags={2}
              noOptionsText="No buildings have been created yet."
              disableCloseOnSelect
              loading={loading_form}
              loadingText={<Loading text="Loading buildings..." height={120} />}
            />
          </Grid>

          <Grid item xs={2}>
            <Button
              type="submit"
              startIcon={loading_submit ? <CircularProgress size={16} /> : <Add />}
              sx={{
                paddingX: 1.5,
              }}
            >
              Add
            </Button>
          </Grid>

          {!loading_form && (
            <Grid item xs={12}>
              <AssignedBuildingList
                assignedBuildings={assignedBuildings.map(({ buildingId, address, assignment }) => ({
                  userId,
                  buildingId,
                  title: address,
                  subtitle: assignment,
                }))}
              />
            </Grid>
          )}
        </Grid>
      </form>
    );
  },
);
